import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { FuseUtils } from './../../../../@fuse/utils';
import { Observable, Subscriber, forkJoin } from 'rxjs';
import { RSA_X931_PADDING } from 'constants';

@Injectable()
export class ChatPanelService {
    contacts: any[];
    chats: any[];
    user: any;

    /**
     * Constructor
     *
     * param {HttpClient} _httpClient
     */
    constructor(
        private _httpClient: HttpClient
    ) {
    }

    /**
     * Loader
     *
     * returns {Observable<any> | any}
     */
    loadContacts(): Observable<any> | any {
        return new Observable(subscriber => {
            forkJoin(
                this.getContacts(),
                this.getUser()
            ).subscribe(
                response => {
                    this.contacts = response[0];
                    this.user = response[1];
                    subscriber.next(null as any);
                },
                error => subscriber.error(error),
                () => subscriber.complete()
            )
        });
    }

    /**
     * Get chat
     *
     * param contactId
     * returns {Observable<any>}
     */
    getChat(contactId): Observable<any> {
        const chatItem = this.user.chatList.find((item) => {
            return item.contactId === contactId;
        });

        // Get the chat
        return new Observable(subscriber => {
            // If there is a chat with this user, return that.
            if (chatItem) {
                this._httpClient.get('api/chat-panel-chats/' + chatItem.chatId)
                    .subscribe(
                        response => subscriber.next(response),
                        error => subscriber.error(error),
                        () => subscriber.complete()
                    );
            }
            // If there is no chat with this user, create one...
            else {
                this.createNewChat(contactId).subscribe(
                    error => subscriber.error(error),
                    () => subscriber.complete(),
                    () => {
                        // and then recall the getChat method
                        this.getChat(contactId).subscribe(
                            chat => {
                                subscriber.next(chat);
                            },
                            error => subscriber.error(error),
                            () => subscriber.complete()
                        );
                    });
            }
        });
    }

    /**
     * Create new chat
     *
     * param contactId
     * returns {Observable<any>}
     */
    createNewChat(contactId): Observable<any> {
        return new Observable(subscriber => {

            // Generate a new id
            const chatId = FuseUtils.generateGUID();

            // Prepare the chat object
            const chat = {
                id: chatId,
                dialog: []
            };

            // Prepare the chat list entry
            const chatListItem = {
                chatId: chatId,
                contactId: contactId,
                lastMessageTime: '2017-02-18T10:30:18.931Z'
            };

            // Add new chat list item to the user's chat list
            this.user.chatList.push(chatListItem);

            // Post the created chat to the server
            this._httpClient.post('api/chat-panel-chats', { ...chat })
                .subscribe(
                    error => subscriber.error(error),
                    () => {

                        // Post the updated user data to the server
                        this._httpClient.post('api/chat-panel-user/' + this.user.id, this.user)
                            .subscribe(() => {
                                subscriber.next(null as any);
                                subscriber.complete();
                            });
                    }
                );
        });
    }

    /**
     * Update the chat
     *
     * param chatId
     * param dialog
     * returns {Observable<any>}
     */
    updateChat(chatId, dialog): Observable<any> {
        return new Observable(subscriber => {

            const newData = {
                id: chatId,
                dialog: dialog
            };

            this._httpClient.post('api/chat-panel-chats/' + chatId, newData)
                .subscribe(
                    response => subscriber.next(response),
                    error => subscriber.error(error),
                    () => subscriber.complete()
                );
        });
    }

    /**
     * Get contacts
     *
     * returns {Observable<any>}
     */
    getContacts(): Observable<any> {
        return new Observable(subscriber => {
            this._httpClient.get('api/chat-panel-contacts')
                .subscribe(
                    response => subscriber.next(response),
                    error => subscriber.error(error),
                    () => subscriber.complete()
                );
        });
    }

    /**
     * Get user
     *
     * returns {Observable<any>}
     */
    getUser(): Observable<any> {
        return new Observable(subscriber => {
            this._httpClient.get('api/chat-panel-user')
                .subscribe(
                    response => subscriber.next(response[0]),
                    error => subscriber.error(error),
                    () => subscriber.complete()
                );
        });
    }
}
